import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, Transition, Variants } from 'framer-motion';
import { Tabs, Tab, Box, Typography } from '@mui/material';
import { useThemeContext } from './ThemeWrapper';

import Sidebar from './Sidebar';
import RightPanel from './RightPanel';

// Define the type for the character data (e.g., ['あ', 'a'] or ['日', 'nichi/hi', 'day, sun'])
type CharacterData = [string, string] | [string, string, string];

// Define a type for a section of characters (e.g., main, dakuon, combo)
type CharacterSection = {
    type: 'main' | 'dakuon' | 'combo' | 'small_tsu' | 'long_vowels';
    title?: string; // Optional title for sections like "Dakuon and Handakuon"
    data: CharacterData[];
};

/**
 * Main page for displaying Japanese characters (Hiragana, Katakana, Kanji).
 */
const HiraganaCharactersPage: React.FC = () => {
    const { isDarkTheme } = useThemeContext();
    const [selectedScript, setSelectedScript] = useState<'hiragana' | 'katakana' | 'kanji'>('hiragana');
    const navigate = useNavigate();

    // --- Hiragana Character Data Organized into Sections ---
    const hiraganaSections: CharacterSection[] = [
        {
            type: 'main',
            data: [
                ['あ', 'a'], ['い', 'i'], ['う', 'u'], ['え', 'e'], ['お', 'o'],
                ['か', 'ka'], ['き', 'ki'], ['く', 'ku'], ['け', 'ke'], ['こ', 'ko'],
                ['さ', 'sa'], ['し', 'shi'], ['す', 'su'], ['せ', 'se'], ['そ', 'so'],
                ['た', 'ta'], ['ち', 'chi'], ['つ', 'tsu'], ['て', 'te'], ['と', 'to'],
                ['な', 'na'], ['に', 'ni'], ['ぬ', 'nu'], ['ね', 'ne'], ['の', 'no'],
                ['は', 'ha'], ['ひ', 'hi'], ['ふ', 'fu'], ['へ', 'he'], ['ほ', 'ho'],
                ['ま', 'ma'], ['み', 'mi'], ['む', 'mu'], ['め', 'me'], ['も', 'mo'],
                ['や', 'ya'], ['ゆ', 'yu'], ['よ', 'yo'],
                ['ら', 'ra'], ['り', 'ri'], ['る', 'ru'], ['れ', 're'], ['ろ', 'ro'],
                ['わ', 'wa'], ['を', 'wo'],
                ['ん', 'n'],
            ]
        },
        {
            type: 'dakuon',
            title: 'Dakuon and Handakuon',
            data: [
                ['が', 'ga'], ['ぎ', 'gi'], ['ぐ', 'gu'], ['げ', 'ge'], ['ご', 'go'],
                ['ざ', 'za'], ['じ', 'ji'], ['ず', 'zu'], ['ぜ', 'ze'], ['ぞ', 'zo'],
                ['だ', 'da'], ['ぢ', 'ji'], ['づ', 'zu'], ['で', 'de'], ['ど', 'do'],
                ['ば', 'ba'], ['び', 'bi'], ['ぶ', 'bu'], ['べ', 'be'], ['ぼ', 'bo'],
                ['ぱ', 'pa'], ['ぴ', 'pi'], ['ぷ', 'pu'], ['ぺ', 'pe'], ['ぽ', 'po'],
            ]
        },
        {
            type: 'combo',
            title: 'Combo',
            data: [
                ['きゃ', 'kya'], ['きゅ', 'kyu'], ['きょ', 'kyo'],
                ['ぎゃ', 'gya'], ['ぎゅ', 'gyu'], ['ぎょ', 'gyo'],
                ['しゃ', 'sha'], ['しゅ', 'shu'], ['しょ', 'sho'],
                ['じゃ', 'ja'], ['じゅ', 'ju'], ['じょ', 'jo'],
                ['ちゃ', 'cha'], ['ちゅ', 'chu'], ['ちょ', 'cho'],
                ['にゃ', 'nya'], ['niyu', 'nyu'], ['にょ', 'nyo'],
                ['ひゃ', 'hya'], ['ひゅ', 'hyu'], ['ひょ', 'hyo'],
                ['びゃ', 'bya'], ['びゅ', 'byu'], ['びょ', 'bya'],
                ['ぴゃ', 'pya'], ['ぴゅ', 'pyu'], ['ぴょ', 'pyo'],
                ['みゃ', 'mya'], ['myu', 'myu'], ['みょ', 'myo'],
                ['りゃ', 'rya'], ['ryu', 'ryu'], ['ryo', 'ryo'],
            ]
        },
        {
            type: 'small_tsu',
            title: 'Small っ',
            data: [
                ['っ+k', 'kk'],
                ['っ+s', 'ss'],
                ['っ+t', 'tt'],
                ['っ+p', 'pp'],
            ]
        },
        {
            type: 'long_vowels',
            title: 'Long vowels',
            data: [
                ['ああ', 'aa'],
                ['いい', 'ii'],
                ['うう', 'uu'],
                ['ええ', 'ee'],
                ['おお', 'oo'],
                ['えい', 'ei'],
                ['おう', 'ou'],
            ]
        },
    ];

    // --- Katakana Character Data Organized into Sections ---
    const katakanaSections: CharacterSection[] = [
        {
            type: 'main',
            data: [
                ['ア', 'a'], ['イ', 'i'], ['ウ', 'u'], ['エ', 'e'], ['オ', 'o'],
                ['カ', 'ka'], ['キ', 'ki'], ['ク', 'ku'], ['ケ', 'ke'], ['コ', 'ko'],
                ['サ', 'sa'], ['シ', 'shi'], ['ス', 'su'], ['セ', 'se'], ['ソ', 'so'],
                ['タ', 'ta'], ['チ', 'chi'], ['ツ', 'tsu'], ['テ', 'te'], ['ト', 'to'],
                ['ナ', 'na'], ['ニ', 'ni'], ['ヌ', 'nu'], ['ネ', 'ne'], ['ノ', 'no'],
                ['ハ', 'ha'], ['ヒ', 'hi'], ['フ', 'fu'], ['ヘ', 'he'], ['ホ', 'ho'],
                ['マ', 'ma'], ['ミ', 'mi'], ['ム', 'mu'], ['メ', 'me'], ['モ', 'mo'],
                ['ヤ', 'ya'], ['ユ', 'yu'], ['ヨ', 'yo'],
                ['ラ', 'ra'], ['リ', 'ri'], ['ル', 'ru'], ['レ', 're'], ['ロ', 'ro'],
                ['ワ', 'wa'], ['ヲ', 'wo'],
                ['ン', 'n'],
            ]
        },
        {
            type: 'dakuon',
            title: 'Dakuon and Handakuon',
            data: [
                ['ガ', 'ga'], ['ギ', 'gi'], ['グ', 'gu'], ['ゲ', 'ge'], ['ゴ', 'go'],
                ['ザ', 'za'], ['ジ', 'ji'], ['ズ', 'zu'], ['ゼ', 'ze'], ['ゾ', 'zo'],
                ['ダ', 'da'], ['ヂ', 'ji'], ['ヅ', 'zu'], ['デ', 'de'], ['ド', 'do'],
                ['バ', 'ba'], ['ビ', 'bi'], ['ブ', 'bu'], ['べ', 'be'], ['ぼ', 'bo'],
                ['パ', 'pa'], ['ピ', 'pi'], ['プ', 'pu'], ['ペ', 'pe'], ['ポ', 'po'],
            ]
        },
        {
            type: 'combo',
            title: 'Combo',
            data: [
                ['キャ', 'kya'], ['キュ', 'kyu'], ['キョ', 'kyo'],
                ['ギャ', 'gya'], ['ギュ', 'gyu'], ['ギョ', 'gyo'],
                ['シャ', 'sha'], ['シュ', 'shu'], ['しょ', 'sho'],
                ['ジャ', 'ja'], ['ジュ', 'ju'], ['ジョ', 'jo'],
                ['チャ', 'cha'], ['チュ', 'chu'], ['チョ', 'cho'],
                ['ニャ', 'nya'], ['ニュ', 'nyu'], ['ニョ', 'nyo'],
                ['ヒャ', 'hya'], ['ヒュ', 'hyu'], ['ヒョ', 'hyo'],
                ['ビャ', 'bya'], ['ビュ', 'byu'], ['びょ', 'bya'],
                ['ピャ', 'pya'], ['ピュ', 'pyu'], ['ピョ', 'pyo'],
                ['ミャ', 'mya'], ['ミュ', 'mya'], ['ミョ', 'mya'],
                ['リャ', 'rya'], ['リュ', 'ryu'], ['リョ', 'ryo'],
            ]
        },
        {
            type: 'small_tsu',
            title: 'Small っ',
            data: [
                ['ッ+k', 'kk'],
                ['ッ+s', 'ss'],
                ['ッ+t', 'tt'],
                ['ッ+p', 'pp'],
            ]
        },
        {
            type: 'long_vowels',
            title: 'Long vowels',
            data: [
                ['アー', 'aa'],
                ['イー', 'ii'],
                ['ウー', 'uu'],
                ['エー', 'ee'],
                ['オー', 'oo'],
                ['エイ', 'ei'],
                ['オウ', 'ou'],
            ]
        },
    ];

    // --- Kanji Character Data (remains flat as it doesn't have the same groupings) ---
    const kanji: CharacterData[] = [
        ['日', 'nichi/hi', 'day, sun'],
        ['一', 'ichi', 'one'],
        ['国', 'kuni', 'country'],
        ['人', 'hito', 'person'],
        ['年', 'nen', 'year'],
        ['大', 'dai', 'big'],
        ['十', 'juu', 'ten'],
        ['二', 'ni', 'two'],
        ['本', 'hon', 'book, origin'],
        ['中', 'naka', 'inside, middle'],
        ['長', 'naga', 'long, leader'],
        ['出', 'deru', 'exit, go out'],
        ['三', 'san', 'three'],
        ['時', 'ji', 'time, hour'],
        ['行', 'iku', 'go'],
        ['見', 'miru', 'see'],
        ['月', 'getsu/tsuki', 'month, moon'],
        ['後', 'ato', 'after, behind'],
        ['前', 'mae', 'before, in front'],
        ['生', 'sei', 'life, birth'],
        ['五', 'go', 'five'],
        ['間', 'aida', 'interval, space'],
        ['上', 'ue', 'up, above'],
        ['東', 'higashi', 'east'],
        ['四', 'shi/yon', 'four'],
        ['今', 'ima', 'now'],
        ['金', 'kin', 'gold, money'],
        ['九', 'kyuu', 'nine'],
        ['入', 'hairu', 'enter'],
        ['学', 'gaku', 'study, learning'],
        ['高', 'takai', 'tall, high'],
        ['円', 'en', 'yen, circle'],
        ['子', 'ko', 'child'],
        ['外', 'soto', 'outside'],
        ['八', 'hachi', 'eight'],
        ['六', 'roku', 'six'],
        ['下', 'shita', 'down, below'],
        ['来', 'kuru', 'come'],
        ['気', 'ki', 'spirit, mind'],
        ['小', 'chiisai', 'small'],
        ['七', 'nana', 'seven'],
    ];

    /**
     * Plays an audio file for a given romaji character.
     * Uses process.env.PUBLIC_URL for robust path resolution.
     */
    const playAudio = (romaji: string) => {
        if (!romaji) return;
        const cleanRomaji = romaji.split('/')[0].toLowerCase().trim();
        const audio = new Audio(`${process.env.PUBLIC_URL}/audio/${cleanRomaji}.mp3`);
        audio.play().catch(err => {
            console.error(`Audio file not found at: ${process.env.PUBLIC_URL}/audio/${cleanRomaji}.mp3`, err);
        });
    };

    // Determine which character set (sections) to display based on state
    let currentCharacterSections: CharacterSection[] = [];
    if (selectedScript === 'hiragana') {
        currentCharacterSections = hiraganaSections;
    } else if (selectedScript === 'katakana') {
        currentCharacterSections = katakanaSections;
    } else if (selectedScript === 'kanji') {
        currentCharacterSections = [{ type: 'main', data: kanji }];
    }

    // Framer Motion variants for staggered grid animation
    const containerVariants: Variants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.05,
            },
        },
    };

    // Correctly typed spring transition for Framer Motion
    const springTransition: Transition = {
        type: "spring",
        stiffness: 100,
        damping: 10
    };

    // Framer Motion variants for individual character boxes
    const itemVariants: Variants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: springTransition,
        },
    };

    // Function to handle navigation to the correct tips page
    const handleTipsNavigation = () => {
        if (selectedScript === 'hiragana') {
            navigate('/hiragana101');
        } else if (selectedScript === 'katakana') {
            navigate('/katakana101'); // Navigate to Katakana101
        } else if (selectedScript === 'kanji') {
            navigate('/kanji101'); // Navigate to Kanji101
        }
    };

    // Function to handle navigation to the correct tracing page
    const handleLearnCharactersNavigation = () => {
        navigate(`/trace-characters/${selectedScript}`);
    };

    return (
        // Main container: Controls overall page background and text color
        // This div is wrapped by ThemeProvider in App.tsx (or similar top-level file)
        <div className={`flex ${isDarkTheme ? 'bg-black text-white' : 'bg-white text-black'} min-h-screen font-inter`}>
            {/* Desktop Sidebar (now a separate component) */}
            <Sidebar />

            {/* Main Content Area - Fixed mobile padding and spacing */}
            <main className={`flex-1 px-3 py-4 pb-20 sm:px-6 sm:py-6 md:px-8 md:py-8 lg:px-12 lg:py-12 overflow-y-auto ${isDarkTheme ? 'bg-black text-white' : 'bg-white text-black'}`}>
                {/* Script Selection Tabs using MUI Tabs - Improved mobile */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: { xs: 2, sm: 3, md: 4 } }}>
                    <Tabs
                        value={selectedScript}
                        onChange={(e, newValue) => setSelectedScript(newValue)}
                        aria-label="japanese scripts tabs"
                        textColor="inherit"
                        TabIndicatorProps={{ style: { backgroundColor: isDarkTheme ? '#34d399' : '#059669' } }}
                        variant="scrollable" // Enable horizontal scrolling for tabs
                        scrollButtons="auto" // Show scroll buttons automatically
                        sx={{
                            '& .MuiTab-root': {
                                minWidth: { xs: 'auto', sm: 160 },
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                fontWeight: 600,
                            }
                        }}
                    >
                        {/* Tab text color is handled dynamically based on theme */}
                        <Tab label="Hiragana" value="hiragana" sx={{ color: isDarkTheme ? 'white' : 'black' }} />
                        <Tab label="Katakana" value="katakana" sx={{ color: isDarkTheme ? 'white' : 'black' }} />
                        <Tab label="Kanji" value="kanji" sx={{ color: isDarkTheme ? 'white' : 'black' }} />
                    </Tabs>
                </Box>

                {/* Introduction Section - Improved mobile typography */}
                <div className="mb-4 sm:mb-6 lg:mb-8">
                    <Typography 
                        variant="h3" 
                        component="h1" 
                        className={`!font-extrabold mb-2 !text-xl sm:!text-2xl md:!text-3xl lg:!text-4xl ${isDarkTheme ? 'text-white' : 'text-black'}`}
                        sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem', lg: '3rem' } }}
                    >
                        Let's learn{' '}
                        {selectedScript === 'hiragana' ? 'Hiragana' : selectedScript === 'katakana' ? 'Katakana' : 'Kanji'}!
                    </Typography>
                    <Typography 
                        variant="body1" 
                        component="p" 
                        className={`text-sm sm:text-base lg:text-lg mb-3 sm:mb-4 lg:mb-6 ${isDarkTheme ? 'text-gray-300' : 'text-gray-700'}`}
                    >
                        {selectedScript === 'kanji' ? 'Get to know the most common Kanji.' : 'Get to know the main writing system in Japanese.'}
                    </Typography>
                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 lg:space-x-4">
                        <motion.button
                            className={`font-bold py-2 px-3 sm:py-3 sm:px-6 rounded-lg sm:rounded-xl shadow-md border-2 transition-colors duration-300 text-sm sm:text-base ${isDarkTheme ? 'bg-zinc-800 text-emerald-400 border-emerald-400' : 'bg-gray-200 text-emerald-600 border-emerald-600'}`}
                            onClick={handleTipsNavigation}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            TIPS
                        </motion.button>
                        <motion.button
                            className={`font-bold py-2 px-3 sm:py-3 sm:px-6 rounded-lg sm:rounded-xl shadow-lg transition-colors duration-300 text-sm sm:text-base ${isDarkTheme ? 'bg-emerald-500 text-white' : 'bg-emerald-600 text-white'}`}
                            onClick={handleLearnCharactersNavigation}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            LEARN THE CHARACTERS
                        </motion.button>
                    </div>
                </div>

                {/* Characters Grid Sections with Framer Motion - Optimized mobile spacing */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={selectedScript}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        variants={containerVariants}
                    >
                        {currentCharacterSections.map((section, sectionIndex) => (
                            <React.Fragment key={section.type}>
                                {section.title && (
                                    <Typography
                                        variant="h4"
                                        component="h2"
                                        className={`!font-extrabold mt-4 sm:mt-6 lg:mt-8 mb-2 sm:mb-3 lg:mb-4 !text-lg sm:!text-xl md:!text-2xl lg:!text-3xl ${isDarkTheme ? 'text-white' : 'text-black'}`}
                                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem', lg: '2.5rem' } }}
                                    >
                                        {section.title}
                                    </Typography>
                                )}
                                <motion.div
                                    // Responsive grid: 5 columns on all breakpoints
                                    className={`grid grid-cols-5 gap-1 sm:gap-2 md:gap-3 lg:gap-4 xl:gap-6 ${section.title ? 'mt-2 sm:mt-3 lg:mt-4' : ''} mb-4 sm:mb-6 lg:mb-8`}
                                >
                                    {section.data.map((item, charIndex) => (
                                        <motion.div
                                            key={`${section.type}-${charIndex}`}
                                            className={`p-1.5 sm:p-2 md:p-3 lg:p-4 rounded-md sm:rounded-lg lg:rounded-xl shadow-lg border-2 cursor-pointer transition-transform duration-200 flex flex-col items-center ${isDarkTheme ? 'bg-zinc-900 border-gray-700 hover:border-emerald-400' : 'bg-white border-gray-200 hover:border-emerald-600'}`}
                                            whileHover={{ scale: 1.05, y: -2 }}
                                            whileTap={{ scale: 0.95 }}
                                            variants={itemVariants}
                                            onClick={() => playAudio(item[1])}
                                        >
                                            {/* Character (item[0]) with responsive font size */}
                                            <div className={`text-2xl sm:text-2xl md:text-3xl lg:text-3xl font-bold text-center mb-0.5 sm:mb-1 ${isDarkTheme ? 'text-white' : 'text-black'}`}>{item[0]}</div>
                                            {/* Romaji (item[1]) with responsive font size */}
                                            <div className={`text-xs sm:text-sm text-center mb-0.5 sm:mb-1 leading-tight ${isDarkTheme ? 'text-gray-300' : 'text-gray-700'}`}>{item[1]}</div>
                                            {selectedScript === 'kanji' && item[2] && (
                                                // Meaning (item[2]) with responsive font size
                                                <div className={`text-xxs sm:text-xs text-center leading-tight ${isDarkTheme ? 'text-gray-400' : 'text-gray-600'}`}>{item[2]}</div>
                                            )}
                                            <div className={`h-0.5 sm:h-1 rounded-full mt-0.5 sm:mt-1 lg:mt-2 ${isDarkTheme ? 'bg-gray-700' : 'bg-gray-300'}`}>
                                                <div className={`h-full w-0 rounded-full transition-all duration-500 ${isDarkTheme ? 'bg-emerald-400' : 'bg-emerald-600'}`}></div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </motion.div>
                                </React.Fragment>
                            ))}
                        </motion.div>
                    </AnimatePresence>
                </main>

                {/* Desktop Right Panel (now a separate component) */}
                <RightPanel />
            </div>
    );
};

export default HiraganaCharactersPage;